import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import roboto400 from '../../assets/fonts/Roboto_400.ttf';
import roboto500 from '../../assets/fonts/Roboto_500.ttf';
import roboto700 from '../../assets/fonts/Roboto_700.ttf';

import { getInvoiceSampleData, MILEAGE_UNIT } from '../../utility/Constant';
import { formatMoney, getAppliedTaxNames, getContrastFontColor, isMultiTaxPayload } from '../../utility/helpers/commonFunction';
import TaxSummaryTable from './TaxSummaryTable';

Font.register({
    family: 'Roboto-400',
    src: roboto400,
    fontWeight: 400,
});
Font.register({
    family: 'Roboto-500',
    src: roboto500,
    fontWeight: 500,
});
Font.register({
    family: 'Roboto-700',
    src: roboto700,
    fontWeight: 700,
});

const hoverColor = '#F3F3F3';

const appendMileageUnit = (value, mileageUnit, showUnit) => {
    if (!value) return value;
    if (!showUnit) return value;
    const str = String(value);
    if (str.toLowerCase().includes('km') || str.toLowerCase().includes('mi')) return str;
    return `${str} ${mileageUnit}`;
};

const shouldShowServiceSubtotal = (service) => {
    if (service?.pricingType === 'fixed') return false;
    const items = service?.items || [];
    const hasLabour = items.some((item) => item.type === 'labor');
    const partCount = items.filter((item) => item.type === 'parts' || item.type === 'tire').length;
    return (hasLabour && partCount > 0) || partCount > 1;
};

const shouldShowDiscountAmount = (amount) => {
    if (amount == null || amount === '') return false;
    if (typeof amount === 'number') return amount !== 0;
    const s = String(amount).trim();
    return s !== '0' && s !== '$0.00' && s !== '0.00';
};

const formatDiscountDisplay = (amount) => {
    if (typeof amount === 'number') return `($${amount.toFixed(2)})`;
    return `(${String(amount)})`;
};

const shouldHighlightRow = (rowIndex) => rowIndex === 0 || (rowIndex > 0 && rowIndex % 2 === 0);

const InvoiceDesign3 = ({ templateSettings, configData }) => {
    const color1 = templateSettings?.color1;
    const color2 = templateSettings?.color2 || getContrastFontColor(templateSettings?.color1);
    const color3 = templateSettings?.color3;
    const color4 = templateSettings?.color4;
    const color5 = getContrastFontColor(templateSettings?.color4);

    const displaySettings = templateSettings?.display?.invoice || {};

    // Shop country picks the mock payload: US shops preview US taxes, everyone else the Canadian set.
    const countryCode = templateSettings?.countryCode;
    const isUsShop =
        String(countryCode || '')
            .trim()
            .toUpperCase() === 'US';
    const sampleData = getInvoiceSampleData(countryCode);

    // --- Multi-tax payload support (taxesBreakdown[]). Legacy payload => isMultiTax false => nothing extra renders. ---
    const isMultiTax = isMultiTaxPayload(sampleData?.totals);
    const showTaxColumnInTable = isMultiTax && displaySettings?.customer?.taxSummary === true;
    const summaryTaxes = isMultiTax ? sampleData?.totals?.taxesBreakdown || [] : [];
    const showTaxSummaryTable = showTaxColumnInTable && summaryTaxes.length > 0;
    // Table column sizing: numeric + tax columns are a fixed 60pt, description takes the rest.
    const NUMERIC_COL_W = 60;
    const numericCol = { width: NUMERIC_COL_W, flexGrow: 0, flexShrink: 0, textAlign: 'right' };
    const flexCol = { flexGrow: 1, flexShrink: 1, flexBasis: 0, paddingRight: 5 };
    const mileageUnit = configData?.config?.mileageUnit === MILEAGE_UNIT.MILES ? MILEAGE_UNIT.MILES : MILEAGE_UNIT.KM;
    const showMileageUnit = displaySettings?.vehicle?.mileageUnit === true;

    const invoiceData = {
        ...sampleData,
        squareLogo: templateSettings?.shopLogoSquare || sampleData.squareLogo,
        shopName: templateSettings?.shopName || sampleData.shopName,
        shopPhone: templateSettings?.shopPhone || sampleData.shopPhone,
        shopEmail: templateSettings?.shopEmail || sampleData.shopEmail,
        shopWebsite: templateSettings?.shopWebsite || sampleData.shopWebsite,
        dates: sampleData.dates,

        // shopPhone: templateSettings?.shopPhone || sampleData.shopPhone,
        // dates: templateSettings?.dates || sampleData.dates,
        vehicle: {
            ...sampleData.vehicle,
            odoDroppedOff: appendMileageUnit(sampleData.vehicle.odoDroppedOff, mileageUnit, showMileageUnit),
            odoReturned: appendMileageUnit(sampleData.vehicle.odoReturned, mileageUnit, showMileageUnit),
        },
    };

    const styles = StyleSheet.create({
        fontMedium: {
            fontFamily: 'Roboto-500',
            fontWeight: 500,
        },
        fontBold: {
            fontFamily: 'Roboto-700',
            fontWeight: 700,
        },
        page: {
            padding: '20px',
            fontFamily: 'Roboto-400',
            fontWeight: 400,
            fontSize: 10,
            color: '#000000',
        },
        header: {
            flexDirection: 'row',
            marginBottom: 16,
            backgroundColor: color4,
            color: color5,
            padding: 12,
            alignItems: 'center',
        },
        headerLeft: {
            width: '60%',
            flexDirection: 'column',
        },
        headerRight: {
            width: '40%',
            textAlign: 'right',
            color: '#FFFFFF',
        },
        logoSection: {
            flexDirection: 'row',
            marginBottom: 0,
            alignItems: 'center',
        },
        logo: {
            width: 69,
            height: 69,
            backgroundColor: '#000',
        },
        shopDetails: {
            width: '65%',
            marginLeft: 12,
            justifyContent: 'center',
        },
        shopName: {
            fontSize: 20,
            fontFamily: 'Roboto-700',
            fontWeight: 700,
            marginBottom: 2,
            color: color3,
            borderBottomColor: color3,
            borderTopColor: color3,
            letterSpacing: '-0.1px',
        },
        shopAddress: {
            fontSize: 9,
            marginBottom: 2,
            color: color5,
            letterSpacing: '-0.04px',
        },
        shopPhone: {
            fontSize: 9,
            color: color5,
            letterSpacing: '-0.04px',
        },
        invoiceTitle: {
            fontSize: 14,
            fontFamily: 'Roboto-700',
            fontWeight: 700,
            letterSpacing: '-0.21px',
            color: color5,
            textTransform: 'uppercase',
            marginBottom: 2,
        },
        invoiceNumber: {
            fontSize: 12,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
            marginBottom: 2,
            letterSpacing: '-0.06px',
            color: color5,
        },
        headerDetails: {
            fontSize: 10,
            marginBottom: 2,
            color: color5,
            letterSpacing: '-0.05px',
        },
        infoSection: {
            marginTop: 0,
            flexDirection: 'row',
            justifyContent: 'space-between',
        },
        infoColumn: {
            width: '30%',
        },
        infoTitle: {
            fontSize: 14,
            fontFamily: 'Roboto-700',
            fontWeight: 700,
            marginBottom: 3,
        },
        infoDetail: {
            fontSize: 10,
            fontFamily: 'Roboto-400',
            fontWeight: 400,
            marginBottom: 2,
        },
        infoLabel: {
            fontSize: 10,
            fontFamily: 'Roboto-700',
            fontWeight: 700,
        },
        infoValue: {
            fontSize: 10,
            fontFamily: 'Roboto-400',
            fontWeight: 400,
        },
        serviceSection: {
            marginTop: 16,
        },
        serviceHeader: {
            backgroundColor: color1,
            color: color2,
            paddingHorizontal: 16,
            paddingVertical: 9.5,
            fontSize: 14,
            fontFamily: 'Roboto-700',
            fontWeight: 700,
            letterSpacing: '-0.065px',
            textAlign: 'center',
        },
        serviceTableHeader: {
            backgroundColor: color4,
            color: color5,
            flexDirection: 'row',
            borderBottomWidth: 0,
            paddingHorizontal: 16,
            paddingVertical: 3.5,
            fontFamily: 'Roboto-700',
            fontWeight: 700,
            letterSpacing: '-0.055px',
            fontSize: 9,
            borderLeftWidth: 0,
            borderRightWidth: 0,
            borderTopWidth: 0,
            marginTop: -1,
            alignItems: 'center',
        },
        serviceTableRow: {
            flexDirection: 'row',
            borderLeftWidth: 1,
            borderRightWidth: 1,
            borderColor: '#DBDBDB',
            paddingHorizontal: 16,
            paddingVertical: 3.5,
            fontSize: 9,
            lineHeight: '143%',
            alignItems: 'center',
            fontFamily: 'Roboto-500',
            fontWeight: 500,
            letterSpacing: '-0.05px',
        },
        descriptionCol: {
            ...flexCol,
        },
        quantityCol: {
            ...numericCol,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
        },
        costCol: {
            ...numericCol,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
        },
        amountCol: {
            ...numericCol,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
        },
        quantityCol2: {
            ...numericCol,
            color: '#000000',
            fontFamily: 'Roboto-700',
            fontWeight: 700,
            fontSize: 9,
        },
        costCol2: {
            ...numericCol,
            fontFamily: 'Roboto-700',
            color: '#000000',
            fontWeight: 700,
            fontSize: 9,
        },
        amountCol2: {
            ...numericCol,
            fontFamily: 'Roboto-700',
            color: '#000000',
            fontWeight: 700,
            fontSize: 9,
        },
        taxCol: {
            width: NUMERIC_COL_W,
            flexGrow: 0,
            flexShrink: 0,
            textAlign: 'center',
        },
        taxColText: {
            fontSize: 9,
            letterSpacing: '0.17px',
            textAlign: 'center',
        },
        serviceTableTotalRow: {
            flexDirection: 'row',
            borderLeftWidth: 0,
            borderRightWidth: 0,
            borderBottomWidth: 1,
            borderColor: '#DBDBDB',
            paddingHorizontal: 16,
            paddingVertical: 3.5,
            alignItems: 'center',
        },
        serviceSectionTotalAmount: {
            fontSize: 9,
            fontFamily: 'Roboto-700',
            fontWeight: 700,
            color: '#000000',
            letterSpacing: '-0.05px',
            lineHeight: 1,
            textAlign: 'right',
        },
        partDetails: {
            fontFamily: 'Roboto-400',
            fontWeight: 400,
            fontSize: 8,
            color: '#5B5E5A',
            marginTop: 4,
        },
        icon: { width: '8px', height: '8px' },
        partDescription: {
            fontSize: 8,
            color: '#5B5E5A',
            marginTop: 2,
            lineHeight: '137.5%',
            fontFamily: 'Roboto-400',
            fontWeight: 400,
        },
        feesSection: {
            marginTop: 16,
        },
        feesTableHeader: {
            flexDirection: 'row',
            borderBottomColor: '#DBDBDB',
            paddingHorizontal: 16,
            paddingVertical: 3.5,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
            fontSize: 9,
            color: '#FFFFFF',
            letterSpacing: '0.35px',
            backgroundColor: '#000000',
            marginTop: -1,
            alignItems: 'center',
        },
        feesTableRow: {
            flexDirection: 'row',
            paddingHorizontal: 16,
            paddingVertical: 3.5,
            fontSize: 9,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
            borderBottomWidth: 1,
            borderLeftWidth: 1,
            borderRightWidth: 1,
            borderColor: '#DBDBDB',
            backgroundColor: '#fff',
            alignItems: 'center',
        },
        feeDescriptionCol: {
            ...flexCol,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
        },
        feeCostCol: {
            ...numericCol,
        },
        notesBody: {
            paddingHorizontal: 16,
            paddingVertical: 3.5,
            borderLeftWidth: 1,
            borderRightWidth: 1,
            borderBottomWidth: 1,
            borderLeftColor: '#DBDBDB',
            borderRightColor: '#DBDBDB',
            borderBottomColor: '#DBDBDB',
        },
        notesText: {
            fontSize: 9,
            letterSpacing: '-0.05px',
            lineHeight: '143%',
            fontFamily: 'Roboto-400',
            fontWeight: 400,
            color: '#5B5E5A',
        },
        footer: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginTop: 32,
            paddingRight: 16,
        },
        termsConditions: {
            width: '50%',
        },
        warrantyTitle: {
            fontSize: 7,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
            color: '#848484',
            lineHeight: '120%',
            letterSpacing: '0.15px',
        },
        termsConditionsDescription: {
            fontSize: 7,
            fontFamily: 'Roboto-400',
            fontWeight: 400,
            color: '#848484',
            lineHeight: '120%',
            letterSpacing: '0.15px',
        },
        termsConditionsDescriptionAfterTitle: {
            marginTop: 8,
        },

        signatureContainer: {
            marginTop: 20,
        },

        signatureLabel: {
            fontSize: 9,
            color: '#848484',
            marginBottom: 20,
        },

        signatureLine: {
            width: 150,
            borderBottomWidth: 1,
            borderBottomColor: '#848484',
        },

        totalsSection: {
            width: '40%',
            color: '#000000',
            letterSpacing: '-0.045px',
        },
        totalSummaryRow: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        totalSummaryRowGutter: {
            marginTop: 4,
        },
        totalDividerLine: {
            borderBottomWidth: 1,
            borderBottomColor: '#DBDBDB',
            width: '100%',
            marginTop: 8,
            marginBottom: 8,
        },
        totalDividerLineGapped: {
            marginTop: 8,
            marginBottom: 8,
            borderBottomWidth: 1,
            borderBottomColor: '#DBDBDB',
            width: '100%',
        },
        totalsNormalLabel: {
            fontSize: 9,
            fontFamily: 'Roboto-400',
            fontWeight: 400,
            color: '#000000',
            width: '70%',
        },
        totalsNormalValue: {
            fontSize: 9,
            fontFamily: 'Roboto-400',
            fontWeight: 400,
            color: '#000000',
            textAlign: 'right',
            width: '30%',
        },
        totalsSubtotalLabel: {
            fontSize: 9,
            fontFamily: 'Roboto-700',
            fontWeight: 700,
            color: '#000000',
            width: '70%',
        },
        totalsSubtotalValue: {
            fontSize: 9,
            fontFamily: 'Roboto-700',
            fontWeight: 700,
            color: '#000000',
            textAlign: 'right',
            width: '30%',
        },
        totalsGrandLabel: {
            fontSize: 11,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
            color: '#000000',
            width: '70%',
        },
        totalsGrandValue: {
            fontSize: 11,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
            color: '#000000',
            textAlign: 'right',
            width: '30%',
        },
        totalsBalanceLabel: {
            fontSize: 9,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
            color: '#000000',
            width: '70%',
        },
        totalsBalanceValue: {
            fontSize: 9,
            fontFamily: 'Roboto-500',
            fontWeight: 500,
            color: '#000000',
            textAlign: 'right',
            width: '30%',
        },
        fullyPaidChip: {
            marginTop: 8,
            paddingVertical: 4,
            backgroundColor: hoverColor,
            borderRadius: 0,
            alignItems: 'center',
            borderWidth: '0.05px',
            borderStyle: 'solid',
            borderColor: '#E0E0E0',
        },
        fullyPaidChipText: {
            fontFamily: 'Roboto-500',
            fontWeight: 500,
            fontSize: 9,
            color: '#000000',
        },
        pageNumber: {
            position: 'absolute',
            bottom: 10,
            right: 20,
            fontSize: 8,
            color: '#000',
        },
    });

    const ServiceItem = ({ item, rowIndex }) => (
        <>
            <View
                style={{
                    ...styles.serviceTableRow,
                    backgroundColor: shouldHighlightRow(rowIndex) ? hoverColor : 'transparent',
                    borderBottomWidth: item.feeName ? 0 : 1,
                }}
            >
                {item?.type === 'labor' ? (
                    <View style={styles.descriptionCol}>
                        <Text style={styles.fontBold}>Labour</Text>
                    </View>
                ) : (
                    <View style={styles.descriptionCol}>
                        {item.type === 'parts' ? (
                            <Text style={styles.fontBold}>{item?.partName}</Text>
                        ) : (
                            <>{displaySettings?.tire?.tyreName && item?.partName ? <Text style={styles.fontBold}>{item?.partName}</Text> : <></>}</>
                        )}

                        {item.type === 'parts' ? (
                            <>
                                {displaySettings?.part?.partNumber && item.partNumber && <Text style={styles.partDetails}>{item.partNumber}</Text>}
                                {displaySettings?.part?.partBrand && item.brand && (
                                    <View
                                        style={{
                                            ...styles.partDetails,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 2,
                                        }}
                                    >
                                        <Image style={styles.icon} src={'https://i.ibb.co/Hc8mcKL/brand-grey.png'} /> <Text>{item.brand}</Text>
                                    </View>
                                )}
                                {displaySettings?.part?.partProvider && item.partsProvider && (
                                    <View
                                        style={{
                                            ...styles.partDetails,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 2,
                                        }}
                                    >
                                        <Image style={styles.icon} src={'https://i.ibb.co/sp7MJV0m/provider-grey.png'} />
                                        <Text>{item.partsProvider}</Text>
                                    </View>
                                )}
                                {displaySettings?.part?.partDescription && item.description && <Text style={styles.partDescription}>{item.description}</Text>}
                            </>
                        ) : (
                            <>
                                <Text style={styles.partDetails}>{item?.tyreDimension}</Text>
                                {displaySettings?.tire?.tyreBrand && item.brand && (
                                    <View
                                        style={{
                                            ...styles.partDetails,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 2,
                                        }}
                                    >
                                        <Image style={styles.icon} src={'https://i.ibb.co/Hc8mcKL/brand-grey.png'} /> <Text>{item.brand}</Text>
                                    </View>
                                )}
                                {displaySettings?.tire?.tyreModel && item.tyreModel && (
                                    <View
                                        style={{
                                            ...styles.partDetails,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 2,
                                        }}
                                    >
                                        <Image style={styles.icon} src={'https://i.ibb.co/NnbnvTDj/model-grey.png'} /> <Text>{item.tyreModel}</Text>
                                    </View>
                                )}
                                {displaySettings?.tire?.tyreDescription && item.description && <Text style={styles.partDescription}>{item.description}</Text>}
                            </>
                        )}
                    </View>
                )}
                <Text style={styles.quantityCol2}>{item?.quantity || 0}</Text>
                <Text style={styles.costCol2}>{item?.cost || '$0.00'}</Text>
                <Text style={styles.amountCol2}>{item?.amount || '$0.00'}</Text>
                {showTaxColumnInTable && (
                    <View style={styles.taxCol}>
                        {getAppliedTaxNames(item).map((name, i) => (
                            <Text key={i} style={styles.taxColText}>
                                {name}
                            </Text>
                        ))}
                    </View>
                )}
            </View>
            {item.feeName && (
                <View
                    style={{
                        ...styles.serviceTableRow,
                        backgroundColor: shouldHighlightRow(rowIndex + 1) ? hoverColor : 'transparent',
                        paddingTop: 6,
                        borderBottomWidth: 1,
                    }}
                >
                    <View style={styles.descriptionCol}>
                        <Text style={{ ...styles.partDetails, marginTop: 0, ...styles.fontBold }}>{item.feeName}</Text>
                    </View>
                    <Text style={styles.quantityCol2}>{item?.quantity || 0}</Text>
                    <Text style={styles.costCol2}>{item?.feeAmount || '$0.00'}</Text>
                    <Text style={styles.amountCol2}>{item?.totalFeeAmount || '$0.00'}</Text>
                    {showTaxColumnInTable && <View style={styles.taxCol} />}
                </View>
            )}
        </>
    );

    const FeeItem = ({ fee }) => (
        <View style={styles.feesTableRow}>
            <Text style={styles.feeDescriptionCol}>{fee?.description || '-'}</Text>
            <Text style={styles.feeCostCol}>{fee?.cost || '$0.00'}</Text>
            {showTaxColumnInTable && (
                <View style={styles.taxCol}>
                    {getAppliedTaxNames(fee).map((name, i) => (
                        <Text key={i} style={styles.taxColText}>
                            {name}
                        </Text>
                    ))}
                </View>
            )}
        </View>
    );

    const totals = invoiceData?.totals;
    const totalsSummaryItems = [];
    const appendSummaryRow = (variant, label, value) => totalsSummaryItems.push({ type: 'row', variant, label, value });
    const appendSummaryDivider = () => totalsSummaryItems.push({ type: 'divider' });

    if (totals?.laborTotal) appendSummaryRow('normal', 'Labour', totals.laborTotal);
    if (totals?.partsTotal) appendSummaryRow('normal', 'Parts', totals.partsTotal);
    if (displaySettings?.customer?.packages && totals?.servicePackageTotal) {
        appendSummaryRow('normal', 'Service Packages', totals.servicePackageTotal);
    }
    if (totals?.laborTotal || totals?.partsTotal || totals?.servicePackageTotal) appendSummaryDivider();
    if (totals?.subTotal) appendSummaryRow('subtotal', 'Subtotal', totals.subTotal);
    if (totals?.feesTotal) appendSummaryRow('normal', 'Fees', totals.feesTotal);
    if (shouldShowDiscountAmount(totals?.discount?.amount)) {
        const d = totals.discount;
        appendSummaryRow(
            'normal',
            d?.description
                ? `Discount - ${d.description} (${d?.discountPercentage}%)`
                : d?.discountPercentage
                  ? `Discount (${d.discountPercentage}%)`
                  : 'Discount',
            formatDiscountDisplay(d.amount)
        );
    }
    const isSingleTaxLine = invoiceData?.singleTaxLineOnPdf ?? totals?.singleTaxLineOnPdf;
    const combinedTaxLabel = invoiceData?.combinedTaxLabel || totals?.combinedTaxLabel || 'Sales Tax';
    const combinedTaxPercentage = invoiceData?.combinedTaxPercentage ?? totals?.combinedTaxPercentage;
    const combinedTaxAmount = invoiceData?.combinedTaxAmount || totals?.combinedTaxAmount || totals?.tax;

    if (isMultiTax && summaryTaxes.length > 0) {
        if (isSingleTaxLine && isUsShop) {
            const taxLabelText =
                combinedTaxPercentage != null && combinedTaxPercentage !== '' ? `${combinedTaxLabel} (${combinedTaxPercentage}%)` : combinedTaxLabel;
            appendSummaryRow('normal', taxLabelText, formatMoney(combinedTaxAmount));
        } else {
            summaryTaxes.forEach((t) => appendSummaryRow('normal', `${t.taxName} (${t.taxPercentage}%)`, formatMoney(t.taxAmount)));
        }
    } else if (totals?.tax != null && String(totals.tax).trim() !== '') {
        appendSummaryRow('normal', 'Tax', totals.tax);
    }

    const hasSummaryRowsBeforeTotal = totalsSummaryItems.some((x) => x.type === 'row');
    if (hasSummaryRowsBeforeTotal && totalsSummaryItems[totalsSummaryItems.length - 1]?.type !== 'divider') {
        appendSummaryDivider();
    }

    const showPayments = displaySettings?.customer?.payments ?? true;

    const renderServiceRows = (service) => {
        if (service?.pricingType === 'fixed') {
            return (
                <View
                    style={{
                        ...styles.serviceTableRow,
                        borderBottomWidth: 1,
                        backgroundColor: shouldHighlightRow(0) ? hoverColor : 'transparent',
                    }}
                >
                    <View style={styles.descriptionCol}>
                        <Text style={styles.fontMedium}>{service?.serviceDescription || 'Fixed Price'}</Text>
                    </View>
                    <Text style={styles.quantityCol2}> </Text>
                    <Text style={styles.costCol2}> </Text>
                    <Text style={styles.amountCol2}>{service?.fixedPrice || '$0.00'}</Text>
                </View>
            );
        }

        let currentIdx = 0;
        const renderedDescription = service.serviceDescription ? (
            <View
                style={{
                    ...styles.serviceTableRow,
                    borderBottomWidth: 1,
                    backgroundColor: shouldHighlightRow(currentIdx++) ? hoverColor : 'transparent',
                }}
            >
                <View style={styles.descriptionCol}>
                    <Text style={{ ...styles.fontMedium, fontSize: '9px' }}>{service.serviceDescription}</Text>
                </View>
                <Text style={styles.quantityCol2}></Text>
                <Text style={styles.costCol2}></Text>
                <Text style={styles.amountCol2}></Text>
                {showTaxColumnInTable && <View style={styles.taxCol} />}
            </View>
        ) : null;

        const renderedItems =
            service.items?.length > 0
                ? service.items.map((item, itemIndex) => {
                      const rowIndex = currentIdx++;
                      if (item.feeName) {
                          currentIdx++;
                      }
                      return <ServiceItem key={itemIndex} item={item} rowIndex={rowIndex} />;
                  })
                : null;

        const renderedSubtotal = shouldShowServiceSubtotal(service) ? (
            <View style={styles.serviceTableTotalRow}>
                <View style={styles.descriptionCol} />
                <Text style={styles.quantityCol2}> </Text>
                <Text style={styles.costCol2}> </Text>
                <Text style={[styles.amountCol, styles.serviceSectionTotalAmount]}>{service?.serviceTotal ?? service?.fixedPrice ?? '$0.00'}</Text>
                {showTaxColumnInTable && <View style={styles.taxCol} />}
            </View>
        ) : null;

        return (
            <>
                {renderedDescription}
                {renderedItems}
                {renderedSubtotal}
            </>
        );
    };

    return (
        <Document title="Invoice" author="Auto Repair Shop">
            <Page size="A4" style={styles.page}>
                <View style={styles.header} fixed>
                    <View style={styles.headerLeft}>
                        <View style={styles.logoSection}>
                            {invoiceData?.squareLogo && <Image src={invoiceData.squareLogo} style={styles.logo} />}

                            <View style={styles.shopDetails}>
                                {invoiceData?.shopName && <Text style={styles.shopName}>{invoiceData.shopName}</Text>}
                                {invoiceData.shopAddress && <Text style={styles.shopAddress}>{invoiceData.shopAddressLine1}</Text>}
                                {invoiceData.shopAddress && <Text style={styles.shopAddress}>{invoiceData.shopAddressLine2}</Text>}
                                {invoiceData?.shopPhone && <Text style={styles.shopPhone}>{invoiceData.shopPhone}</Text>}
                                {displaySettings?.businessInfo?.shopEmail && invoiceData?.shopEmail && (
                                    <Text style={styles.shopPhone}>{invoiceData.shopEmail}</Text>
                                )}
                                {displaySettings?.businessInfo?.shopWebsite && invoiceData?.shopWebsite && (
                                    <Text style={styles.shopPhone}>{invoiceData.shopWebsite}</Text>
                                )}
                            </View>
                        </View>
                    </View>

                    <View style={styles.headerRight}>
                        <Text style={styles.invoiceTitle}>{invoiceData?.isDraft ? 'INVOICE (DRAFT)' : 'INVOICE'}</Text>

                        {invoiceData?.invoiceNumber && <Text style={styles.invoiceNumber}>{invoiceData.invoiceNumber}</Text>}

                        {displaySettings?.businessInfo?.ein?.visibility && invoiceData?.ein && (
                            <Text style={styles.headerDetails}>
                                {displaySettings?.businessInfo?.ein?.label || 'EIN'}: {invoiceData.ein}
                            </Text>
                        )}

                        {displaySettings?.businessInfo?.gstOrHstNumber?.visibility && invoiceData?.businessRegistrationNumber && (
                            <Text style={styles.headerDetails}>
                                {displaySettings?.businessInfo?.gstOrHstNumber?.label || 'GST/HST#'}: {invoiceData.businessRegistrationNumber}
                            </Text>
                        )}

                        {displaySettings?.businessInfo?.additionalBusinessInfo &&
                            Array.isArray(displaySettings.businessInfo.additionalBusinessInfo) &&
                            displaySettings.businessInfo.additionalBusinessInfo.map(
                                (info, index) =>
                                    info?.visibility &&
                                    (info?.value || invoiceData?.additionalBusinessInfo?.[index]?.value) && (
                                        <Text key={index} style={styles.headerDetails}>
                                            {info?.label || 'Info'}: {info?.value || invoiceData?.additionalBusinessInfo?.[index]?.value}
                                        </Text>
                                    )
                            )}

                        {displaySettings?.businessInfo?.serviceAdvisor && invoiceData?.serviceAdvisor?.name && (
                            <Text style={styles.headerDetails}>Service Advisor: {invoiceData.serviceAdvisor.name}</Text>
                        )}

                        {displaySettings?.businessInfo?.serviceTechnician && invoiceData?.serviceTechnician?.name && (
                            <Text style={styles.headerDetails}>Technician: {invoiceData.serviceTechnician.name}</Text>
                        )}
                    </View>
                </View>

                <View style={styles.infoSection}>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoTitle}>Dates</Text>
                        {invoiceData?.dates?.invoiceDate && (
                            <Text style={styles.infoDetail}>
                                <Text style={styles.infoLabel}>Invoice Date: </Text>
                                {invoiceData.dates.invoiceDate}
                            </Text>
                        )}
                        {invoiceData?.dates?.droppedOff && (
                            <Text style={styles.infoDetail}>
                                <Text style={styles.infoLabel}>Dropped Off: </Text>
                                {invoiceData.dates.droppedOff}
                            </Text>
                        )}
                        {invoiceData?.dates?.workCompletion && (
                            <Text style={styles.infoDetail}>
                                <Text style={styles.infoLabel}>Work Completion: </Text>
                                {invoiceData.dates.workCompletion}
                            </Text>
                        )}
                    </View>

                    <View style={styles.infoColumn}>
                        <Text style={styles.infoTitle}>Customer Information</Text>
                        {invoiceData?.customer?.name && <Text style={styles.infoDetail}>{invoiceData.customer.name}</Text>}
                        {invoiceData?.customer?.company && (
                            <Text style={styles.infoDetail}>
                                <Text style={styles.infoLabel}>Company: </Text>
                                <Text style={styles.infoValue}>{invoiceData.customer.company}</Text>
                            </Text>
                        )}
                        {(invoiceData?.customer?.mobilePhone || invoiceData?.customer?.phone) && (
                            <Text style={styles.infoDetail}>
                                <Text style={styles.infoLabel}>Phone: </Text>
                                <Text style={styles.infoValue}>{invoiceData.customer.mobilePhone || invoiceData.customer.phone}</Text>
                            </Text>
                        )}
                        {invoiceData?.customer?.email && (
                            <Text style={styles.infoDetail}>
                                <Text style={styles.infoLabel}>Email: </Text>
                                <Text style={styles.infoValue}>{invoiceData.customer.email}</Text>
                            </Text>
                        )}
                        {displaySettings?.customer?.customerAddress &&
                            (invoiceData?.customer?.customerAddressLine1 || invoiceData?.customer?.customerAddressLine2) && (
                                <Text style={styles.infoDetail}>
                                    <Text style={styles.infoLabel}>Address: </Text>
                                    <Text style={styles.infoValue}>
                                        {[invoiceData.customer.customerAddressLine1, invoiceData.customer.customerAddressLine2].filter(Boolean).join(', ')}
                                    </Text>
                                </Text>
                            )}
                        {invoiceData?.customer?.poNumber && (
                            <Text style={styles.infoDetail}>
                                <Text style={styles.infoLabel}>PO #: </Text>
                                <Text style={styles.infoValue}>{invoiceData.customer.poNumber}</Text>
                            </Text>
                        )}
                        {invoiceData?.customer?.taxExemptionNumber && (
                            <Text style={styles.infoDetail}>
                                <Text style={styles.infoLabel}>Tax Exemption #: </Text>
                                <Text style={styles.infoValue}>{invoiceData.customer.taxExemptionNumber}</Text>
                            </Text>
                        )}
                    </View>

                    <View style={styles.infoColumn}>
                        <Text style={styles.infoTitle}>Vehicle Information</Text>
                        {invoiceData?.displayVehicle && <Text style={styles.infoDetail}>{invoiceData.displayVehicle}</Text>}
                        {displaySettings?.vehicle?.vin && invoiceData?.vehicle?.vin && (
                            <Text style={styles.infoDetail}>
                                <Text style={styles.infoLabel}>VIN: </Text>
                                <Text style={styles.infoValue}>{invoiceData.vehicle.vin}</Text>
                            </Text>
                        )}
                        {displaySettings?.vehicle?.licenseNumber && (
                            <Text style={styles.infoDetail}>
                                <Text style={styles.infoLabel}>Licence Plate: </Text>
                                <Text style={styles.infoValue}>
                                    {invoiceData.vehicle.provinceCode || invoiceData.vehicle.licenseNumber
                                        ? [invoiceData.vehicle.provinceCode, invoiceData.vehicle.licenseNumber].filter(Boolean).join(' ')
                                        : '-'}
                                </Text>
                            </Text>
                        )}
                        {displaySettings?.vehicle?.fleetUnitNumber && invoiceData?.vehicle?.isFleetCustomer && invoiceData?.vehicle?.fleetUnitNumber && (
                            <Text style={styles.infoDetail}>
                                <Text style={styles.infoLabel}>Unit #: </Text>
                                <Text style={styles.infoValue}>{invoiceData.vehicle.fleetUnitNumber}</Text>
                            </Text>
                        )}
                        {displaySettings?.vehicle?.mileageUnit && (invoiceData?.vehicle?.odoDroppedOff || invoiceData?.vehicle?.odoReturned) && (
                            <>
                                <Text style={styles.infoDetail}>
                                    <Text style={styles.infoLabel}>Mileage in: </Text>
                                    <Text style={styles.infoValue}>{invoiceData?.vehicle?.odoDroppedOff ? `${invoiceData.vehicle.odoDroppedOff}` : '-'}</Text>
                                </Text>
                                <Text style={styles.infoDetail}>
                                    <Text style={styles.infoLabel}>Mileage out: </Text>
                                    <Text style={styles.infoValue}>{invoiceData?.vehicle?.odoReturned ? `${invoiceData.vehicle.odoReturned}` : '-'}</Text>
                                </Text>
                            </>
                        )}
                    </View>
                </View>

                {Array.isArray(invoiceData.services) &&
                    invoiceData.services.length > 0 &&
                    invoiceData.services.map((service, index) => (
                        <View key={index} wrap={false} style={styles.serviceSection}>
                            <Text style={styles.serviceHeader}>{service.name}</Text>

                            <View style={styles.serviceTableHeader}>
                                <Text style={[styles.descriptionCol, styles.fontMedium]}>Description</Text>
                                <Text style={styles.quantityCol}>Qty</Text>
                                <Text style={styles.costCol}>Price</Text>
                                <Text style={styles.amountCol}>Amount</Text>
                                {showTaxColumnInTable && <Text style={[styles.taxCol, styles.fontMedium]}>Tax</Text>}
                            </View>

                            {renderServiceRows(service)}
                        </View>
                    ))}

                {Array.isArray(invoiceData.fees) && invoiceData.fees.length > 0 && (
                    <View style={styles.feesSection} wrap={false}>
                        <Text style={styles.serviceHeader}>Fees</Text>

                        <View style={styles.feesTableHeader}>
                            <Text style={[styles.feeDescriptionCol, styles.fontMedium]}>Description</Text>
                            <Text style={styles.feeCostCol}>Amount</Text>
                            {showTaxColumnInTable && <Text style={[styles.taxCol, styles.fontMedium]}>Tax</Text>}
                        </View>

                        {invoiceData.fees.map((fee, index) => (
                            <FeeItem key={index} fee={fee} />
                        ))}
                    </View>
                )}

                {invoiceData?.recommendationNotes && (
                    <View style={styles.feesSection} wrap={false}>
                        <Text style={styles.serviceHeader}>Notes & Recommendations</Text>
                        <View style={styles.notesBody}>
                            <Text style={styles.notesText}>{invoiceData.recommendationNotes}</Text>
                        </View>
                    </View>
                )}

                <View style={styles.footer} wrap={false}>
                    <View style={styles.termsConditions}>
                        {invoiceData?.warrantyTitle && <Text style={styles.warrantyTitle}>{invoiceData.warrantyTitle}</Text>}

                        <Text style={[styles.termsConditionsDescription, invoiceData?.warrantyTitle && styles.termsConditionsDescriptionAfterTitle]}>
                            {invoiceData?.warrantyDescription || ''}
                        </Text>

                        <View style={styles.signatureContainer}>
                            <Text style={styles.signatureLabel}>Signature</Text>
                            <View style={styles.signatureLine} />
                        </View>
                    </View>

                    <View style={styles.totalsSection}>
                        {totalsSummaryItems.map((item, index) => {
                            if (item.type === 'divider') {
                                return <View key={`tot-div-${index}`} style={styles.totalDividerLine} />;
                            }

                            const isSubtotal = item.variant === 'subtotal';
                            const hasGutter = index > 0 && totalsSummaryItems[index - 1].type !== 'divider';

                            return (
                                <View key={`tot-row-${index}`} style={[styles.totalSummaryRow, hasGutter && styles.totalSummaryRowGutter]}>
                                    <Text style={isSubtotal ? styles.totalsSubtotalLabel : styles.totalsNormalLabel}>{item.label}</Text>
                                    <Text style={isSubtotal ? styles.totalsSubtotalValue : styles.totalsNormalValue}>{item.value}</Text>
                                </View>
                            );
                        })}

                        <View style={[styles.totalSummaryRow, styles.totalSummaryRowGutter]}>
                            <Text style={styles.totalsGrandLabel}>Total</Text>
                            <Text style={styles.totalsGrandValue}>{totals?.total || '$0.00'}</Text>
                        </View>

                        {showPayments && invoiceData?.paymentStatus === 'fullyPaid' ? (
                            <>
                                {invoiceData?.payments?.length > 0 && <View style={styles.totalDividerLineGapped} />}
                                {invoiceData.payments.map((p, idx) => (
                                    <View
                                        key={idx}
                                        style={[styles.totalSummaryRow, (idx > 0 || (invoiceData?.payments?.length || 0) > 0) && styles.totalSummaryRowGutter]}
                                    >
                                        <Text style={styles.totalsNormalLabel}>
                                            {p.paymentDate} – {p.paymentMethod}
                                        </Text>
                                        <Text style={styles.totalsNormalValue}>({p.amount})</Text>
                                    </View>
                                ))}
                                <View style={styles.fullyPaidChip}>
                                    <Text style={styles.fullyPaidChipText}>Fully Paid</Text>
                                </View>
                            </>
                        ) : showPayments && invoiceData?.paymentStatus === 'overPaid' ? (
                            <>
                                {invoiceData?.payments?.length > 0 && <View style={styles.totalDividerLineGapped} />}
                                {invoiceData.payments.map((p, idx) => (
                                    <View
                                        key={idx}
                                        style={[styles.totalSummaryRow, (idx > 0 || (invoiceData?.payments?.length || 0) > 0) && styles.totalSummaryRowGutter]}
                                    >
                                        <Text style={styles.totalsNormalLabel}>
                                            {p.paymentDate} – {p.paymentMethod}
                                        </Text>
                                        <Text style={styles.totalsNormalValue}>({p.amount})</Text>
                                    </View>
                                ))}
                                <View style={styles.fullyPaidChip}>
                                    <Text style={styles.fullyPaidChipText}>Over Paid</Text>
                                </View>
                                <View style={styles.totalDividerLineGapped} />
                                <View style={[styles.totalSummaryRow, styles.totalSummaryRowGutter]}>
                                    <Text style={styles.totalsBalanceLabel}>Balance Due</Text>
                                    <Text style={styles.totalsBalanceValue}>{invoiceData?.totalAmountPending}</Text>
                                </View>
                            </>
                        ) : showPayments && invoiceData?.paymentStatus === 'partiallyPaid' ? (
                            <>
                                {invoiceData?.payments?.length > 0 && <View style={styles.totalDividerLineGapped} />}
                                {invoiceData.payments.map((p, idx) => (
                                    <View
                                        key={idx}
                                        style={[styles.totalSummaryRow, (idx > 0 || (invoiceData?.payments?.length || 0) > 0) && styles.totalSummaryRowGutter]}
                                    >
                                        <Text style={styles.totalsNormalLabel}>
                                            {p.paymentDate} – {p.paymentMethod}
                                        </Text>
                                        <Text style={styles.totalsNormalValue}>({p.amount})</Text>
                                    </View>
                                ))}
                                <View style={styles.totalDividerLineGapped} />
                                <View style={[styles.totalSummaryRow, styles.totalSummaryRowGutter]}>
                                    <Text style={styles.totalsBalanceLabel}>Balance Due</Text>
                                    <Text style={styles.totalsBalanceValue}>{invoiceData?.totalAmountPending}</Text>
                                </View>
                            </>
                        ) : (
                            showPayments && (
                                <>
                                    <View style={styles.totalDividerLineGapped} />
                                    <View style={[styles.totalSummaryRow, styles.totalSummaryRowGutter]}>
                                        <Text style={styles.totalsBalanceLabel}>Balance Due</Text>
                                        <Text style={styles.totalsBalanceValue}>{invoiceData?.totalAmountPending}</Text>
                                    </View>
                                </>
                            )
                        )}
                    </View>
                </View>

                {showTaxSummaryTable && (
                    <TaxSummaryTable summaryTaxes={summaryTaxes} totalTax={invoiceData?.totals?.tax || '$0.00'} countryCode={countryCode} />
                )}

                <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} fixed />
            </Page>
        </Document>
    );
};

export default InvoiceDesign3;
